import { useState } from 'react';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { auth } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Mandatory password change. Rendered instead of the app whenever the
 * signed-in user has `mustChangePassword` set — they cannot reach any
 * other screen until they pick a new password. The launch cohort was
 * seeded with shared passwords, so everyone is flagged once.
 */
export function ForcePasswordChangePage() {
  const { user, signOut, updateUser } = useAuth();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (pw.length < 8) return setError('Password must be at least 8 characters.');
    if (pw !== pw2) return setError("The two passwords don't match.");
    setBusy(true);
    try {
      await auth.setPassword(pw);
      updateUser({ mustChangePassword: false });
    } catch (e) {
      setError((e as Error).message || 'Could not update password.');
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-6 shadow-xl">
        <div className="flex items-center gap-2 text-amber-600">
          <ShieldAlert className="h-5 w-5" />
          <h1 className="text-base font-bold">Set a new password</h1>
        </div>
        <p className="mt-2 text-xs text-zinc-500">
          {user?.name ? `Hi ${user.name.split(' ')[0]} — ` : ''}for security,
          choose a private password before you continue. Nobody else should
          know it.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs">New password</Label>
            <Input
              type="password"
              autoFocus
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 8 characters"
              className="mt-1 h-9 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && pw && pw2 && submit()}
            />
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <Input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="Type it again"
              className="mt-1 h-9 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && pw && pw2 && submit()}
            />
          </div>
        </div>

        {error ? <p className="mt-2 text-[11px] text-rose-600">{error}</p> : null}

        <Button
          className="mt-4 w-full"
          disabled={busy || !pw || !pw2}
          onClick={submit}
        >
          <KeyRound className="mr-1.5 h-4 w-4" />
          {busy ? 'Saving…' : 'Save & continue'}
        </Button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-3 w-full text-center text-[11px] text-zinc-400 hover:text-zinc-600"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
